import React from 'react';
import dayjs from 'dayjs';
import { Col, Row } from '../../lib/utils';
import { resolveMembershipName } from '../../lib/membershipDisplay';

const STATUS_STYLE: Record<string, { label: string; bg: string; color: string; border: string }> = {
    ATTENDANCE: { label: '✓ 출석', bg: '#DCFCE7', color: '#15803D', border: '#BBF7D0' },
    NOSHOW: { label: '✕ 노쇼', bg: '#FEE2E2', color: '#B91C1C', border: '#FECACA' },
    UPCOMING: { label: '예정', bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
    CANCELLED: { label: '취소', bg: '#F3F4F6', color: '#6B7280', border: '#E5E7EB' },
    WAITLISTED: { label: '대기', bg: '#FEF3C7', color: '#92400E', border: '#FDE68A' },
};

const SOURCE_STYLE: Record<string, { label: string; bg: string; color: string }> = {
    pt: { label: 'PT', bg: '#EFF6FF', color: '#1D4ED8' },
    group: { label: '그룹', bg: '#ECFDF5', color: '#059669' },
};

const DAY_ACCENT = ['#EFF6FF', '#F0FDF4', '#FFF7ED', '#F5F3FF', '#FEF2F2'];

const formatTimeRange = (start: string, end: string) =>
    `${dayjs(start).format('A h:mm')} ~ ${dayjs(end).format('A h:mm')}`;

export interface MemberClassEntry {
    _id?: string;
    id?: string;
    scheduleId?: string;
    groupScheduleId?: string;
    groupSessionId?: string;
    bookingId?: string;
    startTime: string;
    endTime: string;
    coachId?: string;
    coachName?: string;
    memberId?: string;
    memberName?: string;
    alias?: string;
    title?: string;
    membershipName?: string;
    membershipId?: string;
    ticketName?: string;
    name?: string;
    membership?: {
        name?: string;
        membershipName?: string;
        ticketName?: string;
    };
    membershipInfo?: {
        membership?: {
            name?: string;
        };
    };
    currentSession?: number | null;
    totalSession?: number | null;
    status?: string;
    attendanceStatus?: string;
    source?: 'pt' | 'group';
    sessionId?: string;
}

interface Props {
    date: string;
    classes: MemberClassEntry[];
    accentIdx?: number;
    coachMap?: Record<string, string>;
    onClickRow?: (row: MemberClassEntry) => void;
}

const MemberClassDayCard: React.FC<Props> = ({ date, classes, accentIdx = 0, coachMap, onClickRow }) => {
    if (!classes || classes.length === 0) return null;

    const d = dayjs(date);
    const accent = DAY_ACCENT[accentIdx % DAY_ACCENT.length];

    return (
        <Col
            style={{
                borderRadius: 12,
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
            }}
        >
            {/* 날짜 헤더 */}
            <Row
                style={{
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 14px',
                    backgroundColor: accent,
                    borderBottom: '1px solid #E5E7EB',
                }}
            >
                <span style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>
                    {d.format('M월 D일')}
                </span>
                <span style={{ fontSize: 13, color: '#64748B', fontWeight: 600 }}>
                    ({d.format('ddd')})
                </span>
                <span style={{ fontSize: 12, color: '#94A3B8' }}>·</span>
                <span style={{ fontSize: 13, color: '#475569' }}>
                    총 <b style={{ color: '#2563EB', fontSize: 14 }}>{classes.length}</b>회
                </span>
            </Row>

            {/* 수업 목록 */}
            <Col>
                {classes.map((cls, idx) => {
                    const isGroup = cls.source === 'group';
                    const sourceCfg = isGroup ? SOURCE_STYLE.group : SOURCE_STYLE.pt;
                    const status = cls.status || cls.attendanceStatus || 'UPCOMING';
                    const statusCfg = STATUS_STYLE[status] || STATUS_STYLE.UPCOMING;
                    const membershipName = resolveMembershipName(cls);
                    const displayTitle = cls.title || membershipName || '-';
                    const ticketLabel = membershipName;
                    const coachName =
                        cls.coachName || (cls.coachId ? coachMap?.[cls.coachId] : undefined) || '-';

                    return (
                        <Row
                            key={cls._id || idx}
                            onClick={() => onClickRow?.(cls)}
                            style={{
                                alignItems: 'center',
                                padding: '8px 14px',
                                borderTop: idx === 0 ? 'none' : '1px solid #F3F4F6',
                                gap: 14,
                                cursor: onClickRow ? 'pointer' : 'default',
                                transition: 'background-color 0.12s',
                            }}
                            onMouseEnter={(e) => {
                                if (onClickRow) e.currentTarget.style.backgroundColor = '#F8FAFC';
                            }}
                            onMouseLeave={(e) => {
                                if (onClickRow) e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            {/* PT/그룹 배지 */}
                            <span
                                style={{
                                    padding: '2px 8px',
                                    borderRadius: 5,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    backgroundColor: sourceCfg.bg,
                                    color: sourceCfg.color,
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    minWidth: 34,
                                    textAlign: 'center',
                                }}
                            >
                                {sourceCfg.label}
                            </span>

                            {/* 시간 */}
                            <Row style={{ alignItems: 'center', gap: 6, flex: '0 0 200px' }}>
                                <span style={{ fontSize: 13 }}>🕐</span>
                                <span style={{ fontSize: 14, color: '#1F2937', fontWeight: 600 }}>
                                    {formatTimeRange(cls.startTime, cls.endTime)}
                                </span>
                            </Row>

                            {/* 강사 */}
                            <Row style={{ alignItems: 'center', gap: 4, flex: '0 0 100px' }}>
                                <span style={{ fontSize: 11, color: '#94A3B8' }}>강사</span>
                                <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>{coachName}</span>
                            </Row>

                            {/* 수업명 / 수강권 + 회차 */}
                            <Row style={{ alignItems: 'center', gap: 8, flex: '1 1 auto', minWidth: 0 }}>
                                <span
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: '#111827',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {displayTitle}
                                </span>
                                {ticketLabel && ticketLabel !== displayTitle && (
                                    <span
                                        style={{
                                            padding: '2px 8px',
                                            borderRadius: 5,
                                            fontSize: 11,
                                            fontWeight: 600,
                                            backgroundColor: '#F1F5F9',
                                            color: '#475569',
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {ticketLabel}
                                    </span>
                                )}
                                {!isGroup && cls.currentSession != null && cls.totalSession != null && (
                                    <span style={{ fontSize: 13, color: '#6B7280', fontWeight: 600, flexShrink: 0 }}>
                                        {cls.currentSession}
                                        <span style={{ color: '#9CA3AF', fontWeight: 500 }}>
                                            /{cls.totalSession}회
                                        </span>
                                    </span>
                                )}
                            </Row>

                            {/* 상태 */}
                            <span
                                style={{
                                    padding: '3px 10px',
                                    borderRadius: 999,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    backgroundColor: statusCfg.bg,
                                    color: statusCfg.color,
                                    border: `1px solid ${statusCfg.border}`,
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                }}
                            >
                                {statusCfg.label}
                            </span>
                        </Row>
                    );
                })}
            </Col>
        </Col>
    );
};

export default MemberClassDayCard;
